// [목적] plan-generate-streaming + sse-live-typing Spec 의 BE/FE 공유 zod 스키마 및 타입 단일 정의
// [이유] CLAUDE §6-5 LLM 응답 zod 파싱 강제 정책 + ADR-T8 인터페이스 계약 import 강제.
//         BE PlanGenerateWorker / SectionAssemblerService / SlotGapService 와 FE EventSource 핸들러가
//         동일 스키마를 참조함으로써 환각·구조 깨짐을 FE 진입 전 zod 파싱 단계에서 차단.
// [사이드 이팩트] apps/api 와 apps/web 양쪽에서 import. 스키마 변경 시 양쪽 회귀 검증 필수.
//                 tourapi.ts 의 dayPlanCoVisitInputSchema 를 import (ragCandidateMetaSchema 는 index.ts 경유 노출) —
//                 변경 시 두 스키마 정합 확인 필요.
//                 Wave 1 (sse-live-typing) 변경사항:
//                   1. phaseEventSchema.phase 유니온 5종 → 9종 (ADR-LT6, Q-3)
//                   2. completeEventSchema.portfolioId nullable 화 (R-18, ADR-LT6)
//                   3. slotFillingStateEventSchema 신규 추가 (ADR-LT6)
//                   4. planStreamEventSchema discriminated union 에 slotFillingStateEventSchema 추가
// [수정 시 고려사항] sectionId 유니온('cover'|'day-1'|...|'mood'|'budget'|'map') 변경 시
//                   SectionAssemblerService 분기 로직 + FE 렌더러 동기 갱신 필요.
//                   durationDays 상한(14) 은 DDL plan_items dayIndex max 와 정합 유지.
//                   errorEventSchema.code 추가 시 LlmStreamService fallback 분기도 함께 확장.
//                   completeEventSchema.portfolioId: null = /messages/stream complete,
//                   uuid = /jobs/:id/stream complete — 소비자가 null 분기 필수.
//                   Wave 2 (CPB ADR-CPB6) 변경사항:
//                   1. addonSchema discriminated union 4종 신규 추가 (poi_request/avoid/preference/day_pin)
//                   2. addonsSchema = z.array(addonSchema).max(5) 신규 추가
//                   3. slotFillingStateSchema.addons: addonsSchema.optional().default([]) 추가
//                   4. 파생 타입 Addon / Addons export 신규 추가

import { z } from 'zod';

import { dayPlanCoVisitInputSchema } from './tourapi.js';

// ── 세션 생성 입력 ────────────────────────────────────────────────────────────

// [목적] POST /plan/sessions 요청 바디 zod 스키마
// [이유] AnonOrAuthenticatedGuard 엔드포인트이므로 BE ZodValidationPipe 가 입력 검증에 사용.
//         FE 폼 유효성 검사에도 동일 스키마 재사용(react-hook-form + zodResolver).
// [사이드 이팩트] PlanGenerateController.createSession() 의 @Body() DTO 소스가 됨.
// [수정 시 고려사항] inputText max(1000) 은 LLM slot-extraction 프롬프트 컨텍스트 예산과 연동 —
//                   변경 시 SlotExtractorService 프롬프트 토큰 상한도 함께 조정.
export const createSessionInputSchema = z.object({
  inputText: z.string().min(1).max(1000),
  chips: z
    .array(z.enum(['family', 'couple', 'solo', 'pet', 'accessible', 'active', 'quiet']))
    .max(7)
    .optional(),
  locale: z.enum(['ko', 'en', 'ja', 'zh-CN', 'zh-TW']).default('ko'),
});

// ── Slot 추출 결과 ─────────────────────────────────────────────────────────────

// [목적] LLM function-calling 으로 추출된 Slot 구조 zod 스키마 — AiSession.slots JSONB 와 동기
// [이유] SlotExtractorService 가 LLM 응답을 이 스키마로 파싱하여 환각값(범위 이탈·유니온 외 값)을 차단.
//         RagCandidateService 도 이 타입을 입력으로 받아 TourAPI 쿼리 파라미터를 생성함.
//         ADR-CSF6: 대화형 슬롯 채우기 Spec — destination/headcount/transport/origin 4필드 추가.
//         ADR-FGQ1·ADR-FGQ-OQ2: 날짜·시간대 선택 수집 — timeOfDayPref 신규 필드 추가.
//           필수 게이트 4종(destination/duration/companions/vibe) 불변, 선택 수집으로만 사용.
//         ADR-SFP2 (chat-slot-fidelity-and-preview, 2026-05-30):
//           durationDays 하한을 min(1) → min(0) 으로 변경 — 0 = 당일치기(day-trip) 표현 허용.
//           "당일" 칩 결정적 매핑 경로로 0 이 저장되므로 스키마가 이를 수용해야 함.
//         ADR-SFP1 (chat-slot-fidelity-and-preview, 2026-05-30):
//           budgetNote 신규 필드 — 사용자 예산 원문(예: "30만원") 보존. 표시 우선, budget enum 은 생성 폴백.
//         버그픽스-C (2026-05-30):
//           companionsNote/transportNote 신규 필드 — 동반자·이동수단 사용자 원문 보존. budgetNote 선례 동일 패턴.
//           "친구들과 반려견 1마리" → companionsNote 에 보존, companions enum 은 'friends' 폴백 — 손실 방지.
// [사이드 이팩트] AiSession 엔티티의 slots 컬럼(JSONB) 과 정합 유지 필요.
//                 신규 4필드 추가로 SlotExtractorService.extractSlots() 재구성 블록 + buildSlotsJsonSchema() 동기 갱신 필요.
//                 timeOfDayPref 추가로 T7(SLOT_KEY_TO_FIELD·FOLLOW_UP_FALLBACK_FIXTURE 확장)·buildLlmContext 주입 대상.
//                 budgetNote/companionsNote/transportNote 추가로 SlotExtractorService 재구성 블록 + buildSlotsJsonSchema() 동기 갱신 필요.
//                 FE TripInfoCard 는 companionsNote/transportNote 원문 우선 표시 로직 추가 필요(후속 FE 카드).
// [수정 시 고려사항] durationDays 상한(14) 은 dayPlanSchema.dayIndex max 와 정합.
//                   durationDays = 0 은 당일치기(0박 1일) — 생성 워커는 Math.max(1, durationDays) 정규화 필요(AD-TM2).
//                   startDate null 은 "날짜 미지정" — 폴리오 생성 시 날짜 없이 진행.
//                   vibe/quiet 는 nullish().transform — LLM(qwen3:14b 등)이 미지정 슬롯을
//                   undefined 가 아닌 null 로 반환하므로 .default() 만으로는 잡히지 않는다.
//                   null/undefined 둘 다 기본값으로 강제 coerce (companions 등 다른 필드는 nullable 유지).
//                   신규 4필드(destination/headcount/transport/origin)는 단순 .nullable() —
//                   LLM 미지정 시 null 그대로 유지 (vibe/quiet 의 .nullish().transform 패턴과 다름).
//                   timeOfDayPref 는 z.string().nullable() — 자유 텍스트 선호 표현 허용(enum 미강제).
//                     LLM 출력 예: "오전 활동 선호", "저녁 식사 후 산책" 등 비정형 허용.
//                   budgetNote/companionsNote/transportNote 는 z.string().max(60).nullable().optional() —
//                     LLM 미지정 시 undefined(후방호환). 표시는 원문 우선, enum 은 생성용 폴백으로 유지.
//                   REQUIRED_SLOTS(slot-gap.service.ts) 4종 및 sufficient 판단 변경 금지(T7 에서 선택 슬롯으로만).
export const slotsSchema = z.object({
  // [2026-05-30 크래시 수정] 모든 nullable 필드에 .default(null) 추가 — LLM(특히 약한 모델)이 출력에서
  //   필드 키 자체를 누락(undefined)하면 .nullable() 만으로는 zod 가 "Required"로 거부 → SCHEMA_VIOLATION 으로
  //   생성 잡 전체가 크래시(실로그: path:["origin"] Required). .default(null) 로 누락 키를 null 로 관용하여
  //   모델 무관하게 파싱 안정성 확보. present 값(null/실값)은 그대로 — 후방호환, 다운스트림은 이미 null 처리.
  companions: z.enum(['solo', 'couple', 'family', 'friends', 'pet']).nullable().default(null),
  /**
   * ADR-SFP2 (chat-slot-fidelity-and-preview, 2026-05-30): 하한을 min(1) → min(0) 으로 변경.
   * 0 = 당일치기(day-trip), N = 여행 일수(N박 N+1일이 아닌 N일 총 여행일 기준).
   * 예: 당일=0, 1박2일=2, 2박3일=3, 3박4일=4.
   * 생성 워커(plan-generate.service.ts)는 0 수신 시 Math.max(1, durationDays) 로 1일 일정 생성(AD-TM2).
   */
  durationDays: z.number().int().min(0).max(14).nullable().default(null),
  budget: z.enum(['budget', 'standard', 'premium']).nullable().default(null),
  vibe: z
    .array(z.string())
    .max(10)
    .nullish()
    .transform((v) => v ?? []),
  quiet: z
    .boolean()
    .nullish()
    .transform((v) => v ?? false),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null),
  // ADR-CSF6: 대화형 슬롯 채우기 4필드 — 신규 소비자는 이 필드를 참조, 기존 소비자는 무시해도 안전
  destination: z.string().max(40).nullable().default(null),
  headcount: z.number().int().min(1).max(20).nullable().default(null),
  // [목적] 교통수단 슬롯 — 자가용/대중교통 레거시 2값 + 버스/KTX/기타 3값 + 렌트 1값 확장
  // [이유] WC-AVCF-02: 렌터카 전용 모드 'rent' 추가.
  //         기존 렌터카는 'self_drive' 로 흡수되어 렌트비가 비용에 미반영 — 전용 모드로 분리.
  //         기존 5값(self_drive·public·bus·ktx·etc)은 제거하지 않아 이전 세션 row 파싱 무실패 보장(후방호환).
  //         'public'·'etc'·'self_drive' 는 레거시 보존 값 — 이동비 매핑 폴백 유지.
  //         'bus'·'ktx' 는 WC-CTX-01 신규값 유지.
  //         'rent' 신규 추가: 렌터카 선택 시 rentalCost 조건부 질문 트리거 + transit-cost rent 공식 적용(WC-AVCF-07·08).
  // [사이드 이팩트] slotsSchema 소비자(slot-extractor, plan-generate, FE 슬롯 라벨) 모두 영향.
  //                 DDL 무변경 — slots 컬럼이 jsonb 이므로 enum 확장 시 마이그레이션 불필요.
  //                 이동비 매핑: self_drive=자가용 / bus·public·etc=대중교통(버스) / ktx=KTX / rent=렌트(렌트비만, v1).
  //                 transit-cost.util.ts TransportMode 및 calcRawCost exhaustive switch 동기 갱신 필요(WC-AVCF-08).
  //                 slot-extractor.service.ts TRANSPORT_CHIP_TO_ENUM 에 "렌트"→'rent' 추가 필요(WC-AVCF-05).
  //                 FE slotLabels.ts TRANSPORT_LABEL.rent 추가 필요(WC-AVCF-09).
  // [수정 시 고려사항] 값 제거 절대 금지 — 기존 세션 데이터 파싱 무실패 유지.
  //                   'rent' 선택 시 rentalCost 슬롯 조건부 되묻기 — 컨트롤러 분기(WC-AVCF-07) 담당.
  //                   transit-cost.util.ts calcRawCost 의 exhaustive switch 에 'rent' 케이스 누락 시 tsc 오류 발생.
  transport: z.enum(['self_drive', 'public', 'bus', 'ktx', 'etc', 'rent']).nullable().default(null),
  origin: z.string().max(40).nullable().default(null),
  /**
   * ADR-FGQ1·ADR-FGQ-OQ2: 시간대 선호 선택 수집 필드.
   * 자유 텍스트 허용(z.string().nullable()) — "오전 중심", "저녁 식사 포함" 등 비정형 선호 표현 수용.
   * 필수 게이트(destination/duration/companions/vibe) 외 선택 수집 — 미수집 시 null 유지, 생성 진행 가능.
   * 수집값은 buildLlmContext / buildDayUserPrompt 에 주입되어 LLM 밴드 배치에 반영(T7 책임).
   */
  timeOfDayPref: z.string().nullable().optional(),
  /**
   * ADR-SFP1 (chat-slot-fidelity-and-preview, 2026-05-30): 사용자 예산 원문 보존 필드.
   * 사용자가 말한 예산 원문(예: "30만원", "하루 5만원", "넉넉히")을 LLM 변형 없이 그대로 보존.
   * 표시 우선 — FE 에코백·TripInfoCard 는 budgetNote 원문 우선 표시, 없으면 budget enum 라벨 폴백.
   * budget enum 은 생성용 폴백으로 유지(LLM 폴리오 생성 시 budget 사용, budgetNote 는 미사용).
   * nullable().optional(): LLM 미지정 시 undefined, 기존 슬롯 데이터(budgetNote 없는 세션)와 후방호환.
   */
  budgetNote: z.string().max(60).nullable().optional(),
  /**
   * 버그픽스-C (2026-05-30): 동반자 원문 보존 필드 — budgetNote 선례 동일 패턴.
   * 사용자가 말한 동반자 원문(예: "친구들과 반려견 1마리")을 LLM 변형 없이 그대로 보존.
   * 표시 우선 — FE TripInfoCard 는 companionsNote 원문 우선 표시, 없으면 companions enum 라벨 폴백.
   * companions enum 은 생성 폴백 — 원문과 불일치 허용(예: "반려견 동반" → companions=pet, companionsNote 그대로).
   * nullable().optional(): LLM 미지정 시 undefined, 기존 슬롯 데이터와 후방호환.
   */
  companionsNote: z.string().max(60).nullable().optional(),
  /**
   * 버그픽스-C (2026-05-30): 이동수단 원문 보존 필드 — budgetNote 선례 동일 패턴.
   * 사용자가 말한 이동수단 원문(예: "렌터카", "KTX 타고 가서 버스로")을 LLM 변형 없이 보존.
   * 표시 우선 — FE TripInfoCard 는 transportNote 원문 우선 표시, 없으면 transport enum 라벨 폴백.
   * transport enum 은 생성 폴백 — 원문과 불일치 허용.
   * nullable().optional(): LLM 미지정 시 undefined, 기존 슬롯 데이터와 후방호환.
   */
  transportNote: z.string().max(60).nullable().optional(),
  // [목적] 렌터카 총 비용 슬롯 — transport='rent' 선택 시 조건부 되묻기로 수집
  // [이유] WC-AVCF-02: 렌터카 렌트비를 이동비에 반영하기 위한 전용 슬롯 신규 추가.
  //         기존 'self_drive' 흡수 방식에서는 주유/톨만 산출되고 렌트비가 누락됨.
  //         budgetNote/transportNote 선례 동일 패턴(nullable().optional()) 적용 —
  //           LLM 미지정 시 undefined, 기존 세션 데이터(rentalCost 없는 row) 파싱 무실패 보장(후방호환).
  //           .default(null) 대신 .optional() 사용: .default() 적용 시 zod output type이 non-optional이 되어
  //           기존 픽스처(rentalCost 없는 객체 리터럴)가 TS2741(required missing) 오류를 냄 — 후방호환 파괴.
  //         transit-cost.util.ts calcRawCost rent 분기가 이 값을 직접 소비(WC-AVCF-08).
  // [사이드 이팩트] slot-gap.service.ts SLOT_KEY_TO_FIELD·FOLLOW_UP_FALLBACK_FIXTURE 에 'rentalCost' 추가 필요(WC-AVCF-06).
  //                 plan-generate.service.ts estimateTransitCost 호출부에 rentalCost 전달 필요(WC-AVCF-08).
  //                 FE TripInfoCard/슬롯 라벨에 rentalCost 에코백 표시 필요(WC-AVCF-09).
  // [수정 시 고려사항] 단위 원(KRW), 정수 — junk 문자열("20만원" 등)은 SlotExtractorService coerceJunkToNull 로 null 처리(WC-AVCF-05).
  //                   transport!=='rent' 이더라도 null/undefined 로 유지 — 다운스트림은 transport 값과 함께 조건 처리.
  //                   v1: 렌트비만 산출(주유·톨 미합산) — 향후 합산 확장 시 이 필드 의미 변경 금지.
  //                   undefined 와 null 구분: undefined = 수집 미시도, null = 수집했으나 미응답(junk coerce 결과).
  rentalCost: z.number().int().min(0).nullable().optional(),
  /**
   * WC-AVCF-02: 렌트 비용 원문 보존 필드 — budgetNote/transportNote 선례 동일 패턴.
   * 사용자가 말한 렌트 비용 원문(예: "20만원", "왕복 15만원")을 LLM 변형 없이 보존.
   * 표시 우선 — FE 에코백은 rentalCostNote 원문 우선 표시, 없으면 rentalCost 숫자 폴백.
   * rentalCost 는 transit-cost 계산용, rentalCostNote 는 표시용 — 두 필드 독립 운용.
   * nullable().optional(): LLM 미지정 시 undefined, 기존 슬롯 데이터와 후방호환.
   */
  rentalCostNote: z.string().max(60).nullable().optional(),
  /**
   * T1 (2026-05-31, folio-schedule-realism-departure-time): 출발 시각 슬롯 신규 추가.
   * 사용자가 말한 출발 시각을 HH:MM 24시간 형식으로 수집 (예: "13:00", "08:30").
   * Day1 시작시각 계산에 사용: departureTime + 교통수단별 이동 버퍼 → Day1 첫 아이템 시각.
   * nullable().optional(): LLM 미지정 시 undefined, 기존 세션 데이터(departureTime 없는 row) 파싱 무실패 보장(후방호환).
   *   .default() 금지 — rentalCost 선례(plan-stream.ts:164-174): .default() 시 zod output type 이 non-optional 이 되어
   *   기존 픽스처(departureTime 없는 객체 리터럴)가 TS2741(required missing) 오류를 냄 → 후방호환 파괴.
   * 추출 정규화: "오후 1시"·"13시" → "13:00". "아침" → "08:00", "점심" → "12:00", "오후" → "14:00", "저녁" → "18:00".
   * 불명확하거나 미언급이면 null. HH:MM regex 미부합 값은 zod 파싱에서 null 폴백(zod 2차 방어).
   * [보안 하드닝 2026-05-31] regex 가 시(00-23)·분(00-59) 범위까지 검증 — '99:99' 같은 형식상 HH:MM 이지만
   *   범위 밖 값이 통과하면 computeDayStartTime 에서 6039분으로 환산돼 Day1 전 아이템 드롭(가용성 저하). 범위 강제로 차단.
   */
  // [목적] 출발 시각 슬롯 — Day1 시작시각 현실화(departureTime + 이동 버퍼)에 사용
  // [이유] 13시 출발이어도 Day1 이 09:30 오전 배정되는 물리적 불가능 일정 방지. REQUIRED_SLOTS 불변, 선택 조건부.
  // [사이드 이팩트] slot-gap.service.ts SLOT_KEY_TO_FIELD/FOLLOW_UP_FALLBACK_FIXTURE 에 'departureTime' 추가 필요.
  //                 slot-extractor.service.ts SCALAR_FIELDS/buildSlotsJsonSchema/extractSlots/mergeExtractSlots 동기 갱신.
  //                 plan-generate.service.ts computeDayStartTime 이 이 값을 소비.
  // [수정 시 고려사항] REQUIRED_SLOTS 에 절대 추가 금지 — 선택 조건부 슬롯 전용.
  //                   .default() 사용 시 후방호환 파괴(TS2741) — 반드시 .optional() 유지.
  departureTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/)
    .nullable()
    .optional(),
  /**
   * chat-web-search-fallback T5 (2026-06-01): 의도 분류 3-way enum — W1 타입 선반영, W2 에서 LLM 추출.
   * optional 로 후방호환 유지 — 기존 AiSession.slots JSONB(intent 없는 row) 파싱 무실패.
   * W2 T6 에서 buildSlotsJsonSchema() 에 intent 추가 후 LLM 이 실제 값 추출.
   * 불명확 시 안전 기본값 "slot"(BE 폴백 — SlotExtractorService W2 T7 책임).
   * optional(): .default() 사용 금지 — departureTime/rentalCost 선례(TS2741 후방호환 파괴 방지).
   */
  intent: z.enum(['slot', 'knowledge_in_domain', 'out_of_scope']).optional(),
});

// ── 섹션 스키마 5종 ────────────────────────────────────────────────────────────

// [목적] Cover 섹션 zod 스키마 — LLM 생성 폴리오 커버 구조
// [이유] SectionAssemblerService 가 LLM stream 완료 후 이 스키마로 파싱하여 DB 영속화 전 환각 차단.
//         FE 는 section SSE 이벤트의 payload 를 이 스키마로 검증 후 Cover 컴포넌트에 바인딩.
// [사이드 이팩트] BE TripPortfolio 엔티티의 커버 필드 구조와 정합 유지 필요.
// [수정 시 고려사항] heroImageUrl 은 S3 presigned URL 또는 TourAPI 이미지 URL — null 허용(기본 이미지 fallback).
//                   vibeTags max(8) 은 FE 태그 렌더러 레이아웃 상한과 연동.
//                   vibeTags 는 nullish().transform — LLM 이 null 반환 시 .default() 가 안 잡으므로 [] 로 coerce.
export const coverSchema = z.object({
  title: z.string().min(1).max(80),
  subtitle: z.string().max(140).nullable(),
  heroImageUrl: z.string().url().max(2000).nullable(),
  vibeTags: z
    .array(z.string())
    .max(8)
    .nullish()
    .transform((v) => v ?? []),
});

// [목적] LLM 스트리밍 생성용 DayPlan 개별 POI 방문 아이템 zod 스키마
// [이유] LLM 이 생성하는 일정 아이템 구조를 zod 로 강제하여 poiId null/invalid·시간 형식 오류 등 환각 차단.
//         folio.ts 의 planItemSchema(조회용, DB id 포함)와 다름 — 생성 출력용으로 id 없음.
//         coVisitInput 필드는 ADR-T8 — tourapi.ts dayPlanCoVisitInputSchema import 강제.
//         fe-be-gap-tier2 T8(ADR-TR2-3): icon 필드 추가 — LLM 추출 emoji 아이콘(max 8자).
//           환각 방어: 누락/파싱 실패 시 null 폴백 → FE PLAN_ITEM_EMOJI_MAP 시각 보장(ADR-T2-4·R-6).
//         ADR-FGQ2·ADR-FGQ-OQ1: timeOfDay 밴드 필드 추가 — 4밴드(오전/점심/오후/저녁).
//           후방호환: nullable().optional() 패턴(icon 선례) — 누락 시 FE 가 scheduledTime 파생 폴백(buildTimeBand).
//           ADR-FGQ-OQ7: plan_items.time_of_day 컬럼(DDL 18)으로 영속화.
//         folio-enrichment AD-1: activity·tip·factInfo 3필드 추가(icon/timeOfDay 선례, .nullable().optional() 후방호환).
//           activity(시간대별 구체 활동)·tip(꿀팁, LLM 생성)·factInfo(사실정보 원문, LLM 변형 금지 AD-2·CLAUDE §4).
//           DDL 19(plan_items activity/tip/fact_info) 컬럼과 1:1 대응. 후속: normalizeDayItemRaw·persistPortfolio 동기 갱신.
// [사이드 이팩트] PlanItem 엔티티 INSERT 소스로 사용. story max(2000) 은 DB text 컬럼 소프트 상한.
//               normalizeDayItemRaw 화이트리스트에 icon·timeOfDay·activity·tip pick 필수(ADR-TR2-3 패턴).
//               timeOfDay 추가로 BE section-assembler·persistPortfolio·FE FolioDayCardPreview·PreviewMiniCard 전파.
//               activity/tip/factInfo 추가로 persistPortfolio(DDL 19)·serializeItem·FE 카드 렌더 전파.
// [수정 시 고려사항] scheduledTime regex 는 HH:MM 24시간 형식 강제.
//                   durationMinutes max(720) = 12시간 — 단일 POI 체류 상한.
//                   poiId null 은 TourAPI 매핑 미완료 아이템(사용자 커스텀 POI 허용).
//                   folio.ts planItemSchema 와 의미 충돌 주의 — 본 스키마는 LLM 생성 출력용.
//                   icon max(8): emoji 1개는 UTF-16 2~4 바이트이나 JS .length 기준 최대 2(서로게이트 쌍).
//                     실제로 단일 emoji는 length 1~2이므로 max(8)은 여유있는 상한.
//                   timeOfDay 4밴드 enum 변경 시 day 프롬프트·툴스키마·FE 그룹 렌더러 모두 동기 갱신 필수.
//                   factInfo 는 LLM 출력이 아닌 RagCandidate/POI 원문에서 직접 주입(AD-2 무변형 봉인).
//                     normalizeDayItemRaw 에서 LLM factInfo 출력 무시·원문 우선 처리 필수.

// [목적] plan_items factInfo 사실정보 jsonb 객체 스키마 (주소/전화/영업시간/주차/휴무/이미지URL)
// [이유] folio-enrichment AD-1·AD-2: 사실 정보를 LLM 변형 없이 원문 그대로 보관하기 위해 별도 스키마 분리.
//         각 필드 nullable().optional() — TourAPI 원본 결측 허용 + 후방호환.
//         버그픽스-ENRICH: imageUrl 필드 추가 — RagCandidate.firstImage 배선(OQ-g 이미지 노출 충족).
//           LLM 이 생성하는 것이 아닌 candidate.firstImage 원문 그대로 주입(AD-2 무변형 봉인).
// [사이드 이팩트] plan_items.fact_info jsonb 컬럼(DDL 19)과 1:1 대응(jsonb 라 DDL 무변경).
//               folio.ts folioItemFactInfoSchema 와 동일 구조 — 두 파일 동기 필수.
//               imageUrl 추가로 FE PlanItemEnrichedContent 썸네일 렌더 경로 활성화.
// [수정 시 고려사항] LLM 이 이 객체를 생성하면 안 됨(AD-2 무변형 원칙). normalizeDayItemRaw 가 LLM 출력 factInfo 를 무시.
//                   pois 컬럼(DDL 20: usetime/parking/restdate) 변경 시 이 스키마도 동기 갱신.
//                   imageUrl 은 TourAPI first_image 원문 URL — FE 렌더 시 null 이면 기본 이미지 폴백.
//                   folio.ts folioItemFactInfoSchema 와 항상 구조 일치 유지(두 파일 동기 의무).
export const planItemFactInfoSchema = z.object({
  addr: z.string().nullable().optional(),
  tel: z.string().nullable().optional(),
  usetime: z.string().nullable().optional(),
  parking: z.string().nullable().optional(),
  restdate: z.string().nullable().optional(),
  /** 버그픽스-ENRICH: RagCandidate.firstImage 원문 배선 — OQ-g 이미지 노출 충족. LLM 생성 금지(AD-2). */
  imageUrl: z.string().nullable().optional(),
  /**
   * [목적] TourAPI content_id(숫자 문자열) 원문 — FE 혼잡도 카드가 GET /pois/:id/congestion 호출 키로 사용.
   * [이유] folio-persist.service.ts(RAG 영속)가 fact_info jsonb 에 이미 contentId 키를 저장 중이었으나
   *         이 스키마가 화이트리스트에 없어 소비 경로가 막혀 있었다(읽기 노출 추가 — DB 쓰기 로직 무변경).
   *         folio.ts folioItemFactInfoSchema 와 구조 동기 필수(두 파일 동기 의무 선례 유지).
   * [사이드 이팩트] 없음 — 필드 추가만, 기존 소비자는 optional 이라 영향 없음.
   * [수정 시 고려사항] LLM 생성 금지(AD-2 원문 무변형) — contentId 는 citation.contentId 원문 그대로.
   */
  contentId: z.string().nullable().optional(),
});
export type PlanItemFactInfo = z.infer<typeof planItemFactInfoSchema>;

export const streamDayPlanItemSchema = z.object({
  poiId: z.string().uuid().nullable(),
  poiName: z.string().min(1).max(120),
  scheduledTime: z
    .string()
    .regex(/^([01]?\d|2[0-3]):[0-5]\d$/)
    .nullable(),
  durationMinutes: z.number().int().min(0).max(720).nullable(),
  story: z.string().max(2000),
  coVisitInput: dayPlanCoVisitInputSchema.optional(), // ADR-T8 import 강제
  /** fe-be-gap-tier2 G6(ADR-T2-4·ADR-TR2-3): LLM 추출 emoji 아이콘. 누락/환각 시 null → FE 타입 폴백 */
  icon: z.string().max(8).nullable().optional(),
  /**
   * ADR-FGQ2·ADR-FGQ-OQ1: 하루 시간대 4밴드 — 오전(morning)/점심(noon)/오후(afternoon)/저녁(evening).
   * 후방호환: nullable().optional() — 구버전 LLM 응답·DB row 에 필드 없어도 zod 파싱 성공.
   * 누락 시 FE 는 scheduledTime 기반 buildTimeBand() 파생 폴백으로 밴드 표시.
   * ADR-FGQ-OQ7: plan_items.time_of_day 컬럼(증분 DDL 18)으로 영속화.
   */
  timeOfDay: z.enum(['morning', 'noon', 'afternoon', 'evening']).nullable().optional(),
  /**
   * folio-enrichment AD-1: 시간대별 구체 활동 추천 (LLM 생성).
   * 후방호환: nullable().optional() — 구버전 row·미배포 환경에서도 parse 통과.
   * plan_items.activity 컬럼(DDL 19)으로 영속화. buildDayToolSchema 에 생성 지시 포함.
   */
  activity: z.string().max(300).nullable().optional(),
  /**
   * folio-enrichment AD-1: 꿀팁 (LLM 이 factInfo 데이터 근거로 생성).
   * 후방호환: nullable().optional() — 구버전 row·미배포 환경에서도 parse 통과.
   * plan_items.tip 컬럼(DDL 19)으로 영속화. 사실 정보 원문 변형 금지(AD-2·CLAUDE §4).
   */
  tip: z.string().max(500).nullable().optional(),
  /**
   * folio-enrichment AD-1·AD-2: 사실정보 원문 (주소/전화/영업시간/주차/휴무).
   * LLM 변형 금지 — normalizeDayItemRaw 에서 LLM 출력 factInfo 를 무시하고 RagCandidate 원문 우선(AD-2 봉인).
   * 후방호환: nullable().optional() — DDL 19 이전 row·미배포 환경에서도 parse 통과.
   * plan_items.fact_info jsonb 컬럼(DDL 19)으로 영속화.
   */
  factInfo: planItemFactInfoSchema.nullable().optional(),
});

// [목적] LLM 스트리밍 생성용 하루 일정 전체 zod 스키마 — Cover 다음 섹션 (day-1 ~ day-N)
// [이유] SectionAssemblerService 가 각 Day 섹션 완료 시 이 스키마로 파싱 후 DayPlan DB 저장.
//         folio.ts 의 dayPlanSchema(조회용, DB id·totalCostEstimate 포함)와 다름 — 생성 출력용.
// [사이드 이팩트] dayIndex 는 1-based. DayPlan 엔티티 dayIndex 컬럼과 정합.
// [수정 시 고려사항] items min(1) — 빈 Day 는 파이프라인 오류로 처리.
//                   items max(20) 은 하루 최대 POI 방문 수 — LLM 프롬프트 제약과 연동.
//                   folio.ts dayPlanSchema 와 의미 충돌 주의 — 본 스키마는 LLM 생성 출력용.
export const streamDayPlanSchema = z.object({
  dayIndex: z.number().int().min(1).max(14),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  title: z.string().max(80),
  items: z.array(streamDayPlanItemSchema).min(1).max(20),
});

// [목적] 지도 섹션 zod 스키마 — 마커 좌표 + bounds
// [이유] FE 지도 렌더러가 이 스키마로 검증된 마커 배열만 렌더링하여 좌표 범위 이탈 방지.
// [사이드 이팩트] Kakao/Naver Maps JS SDK 마커 생성에 직접 사용. lat/lng 범위 검증 없음(강원도 한정 소프트 가이드).
// [수정 시 고려사항] poiId null 은 사용자 커스텀 위치 마커 — DB POI 조회 생략.
//                   dayIndex 는 dayPlanSchema 와 동일 1-based.
export const mapSchema = z.object({
  bounds: z.object({
    north: z.number(),
    south: z.number(),
    east: z.number(),
    west: z.number(),
  }),
  markers: z.array(
    z.object({
      poiId: z.string().uuid().nullable(),
      lat: z.number(),
      lng: z.number(),
      label: z.string().max(80),
      dayIndex: z.number().int().min(1).max(14),
    }),
  ),
});

// [목적] 무드 섹션 zod 스키마 — 컬러 팔레트 + 분위기 텍스트 + 이미지
// [이유] LLM 이 생성하는 팔레트(5색) 와 vibe 문구를 zod 로 강제하여 HEX 형식 오류 차단.
// [사이드 이팩트] FE Mood 섹션 컴포넌트의 paletteHex 배열과 직접 연동.
// [수정 시 고려사항] paletteHex length(5) 고정 — FE 5-슬롯 팔레트 렌더러와 정합.
//                   imageUrls max(8) 은 TourAPI 이미지 최대 반환 수와 연동.
export const moodSchema = z.object({
  paletteHex: z.array(z.string().regex(/^#[0-9A-Fa-f]{6}$/)).length(5),
  vibe: z.string().max(140),
  imageUrls: z.array(z.string().url().max(2000)).max(8),
});

// [목적] "이동" 요약 표시 메타 zod 스키마 — 교통수단 모드·왕복 이동비·편도거리·출발지 명
// [이유] WC-CTX-08: Spec §4-C 4항 "이동 요약 칩/라인(모드·금액·왕복 명시)" 노출 요건.
//        estimateTransitCost(transit-cost.util.ts) 반환 { mode, amountKrw, roundTrip, oneWayKm, originName } 과
//        1:1 대응하여 생성 스트림(budgetSchema) + 폴리오 조회(folioSchema) 양쪽에서 동일 스키마 소비.
//        transport enum 과 정합 — slotsSchema.transport 6값(self_drive|public|bus|ktx|etc|rent) 동일 셋.
//        budget_breakdown 구조(accommodation/food/transport/activity/etc) 는 이 스키마와 별개로 유지.
//        WC-AVCF-02: mode enum 에 'rent' 추가 — slotsSchema.transport 와 동기.
//          rent 선택 시 transitSummary.mode='rent' 로 직렬화되어 FE 이동 요약 칩에 "렌트" 표시.
// [사이드 이팩트] folio.ts folioSchema.transitSummary 가 이 스키마를 import 하여 재사용.
//                WC-CTX-09(생성 연계)가 estimateTransitCost 반환값을 이 스키마로 직렬화.
//                WC-CTX-14(FE)가 이 스키마 타입을 렌더링 소스로 사용.
//                transit-cost.util.ts TransportMode 타입에 'rent' 동기 갱신 필요(WC-AVCF-08).
// [수정 시 고려사항] mode enum 확장 시 slotsSchema.transport, transit-cost.util.ts TransportMode 모두 동기 갱신 필요.
//                   folio.ts 에서 re-export 없이 직접 import — 두 파일 간 순환참조 금지.
//                   amountKrw min(0): 0원 이동비(당일치기·도보 폴백) 허용.
//                   oneWayKm min(0): 편도 거리 0도 유효(같은 시군구 출발·도착).
//                   nullable: transport 슬롯 미입력 시 이동비 산출 skip → transitSummary null 허용.
export const transitSummarySchema = z.object({
  /** 산출에 사용된 교통수단 모드 — slotsSchema.transport enum 과 동일 6값 (WC-AVCF-02: 'rent' 추가) */
  mode: z.enum(['self_drive', 'public', 'bus', 'ktx', 'etc', 'rent']),
  /** 왕복 이동비 (원, 1000원 단위 반올림) — min(0): 0원 이동비 허용 */
  amountKrw: z.number().int().min(0),
  /** 왕복 여부 — Spec §7-g: 항상 true (왕복 기본) */
  roundTrip: z.literal(true),
  /** 편도 도로 추정거리 (km) — haversine × 1.3 보정계수 적용 */
  oneWayKm: z.number().min(0),
  /** 실제 사용된 출발지명 — origin 슬롯 폴백 시 "서울" */
  originName: z.string().min(1).max(40),
});

// [목적] 예산 섹션 zod 스키마 — 총액 + 일별 + 카테고리 분류 + 이동 요약
// [이유] LLM 예산 환각(음수·비현실적 금액)을 zod min(0) 으로 차단. KRW 고정으로 통화 환각 방지.
//        WC-CTX-08: transitSummary 필드 추가 — 이동 요약 표시 메타(모드·금액·왕복).
//          nullable().optional() 선례(icon/timeOfDay/budgetNote) 패턴으로 후방호환 보장.
//          budget_breakdown.transport 합산은 WC-CTX-09 소관 — 이 스키마는 표시 메타만.
// [사이드 이팩트] FE 예산 섹션 차트(일별 bar + 카테고리 도넛)에 직접 바인딩.
//                FE WC-CTX-14: transitSummary 유무에 따라 "이동" 요약 칩/라인 조건부 렌더.
// [수정 시 고려사항] 다국어(locale) 도입 시 currency 유니온 확장 필요.
//                   perDay dayIndex 는 1-based. totalEstimated 는 perDay 합계와 일치하도록 LLM 프롬프트 제약.
//                   breakdown 구조(5카테고리) 변경 시 plan-generate.service.ts normalizeBudgetRaw 동기 갱신.
//                   transitSummary 는 breakdown 과 별개 — breakdown.transport 합산 로직(WC-CTX-09)과 독립.
export const budgetSchema = z.object({
  currency: z.literal('KRW'),
  totalEstimated: z.number().int().min(0),
  perDay: z.array(
    z.object({
      dayIndex: z.number().int().min(1).max(14),
      amount: z.number().int().min(0),
    }),
  ),
  breakdown: z.object({
    accommodation: z.number().int().min(0),
    food: z.number().int().min(0),
    transport: z.number().int().min(0),
    activity: z.number().int().min(0),
    etc: z.number().int().min(0),
  }),
  // WC-CTX-08: 이동 요약 표시 메타 — 후방호환(nullable/optional). transport 슬롯 미입력 시 null.
  // 합산 로직은 WC-CTX-09 소관 — 이 필드는 표시 전용.
  transitSummary: transitSummarySchema.nullable().optional(),
});

// ── RAG 후보 메타 (ADR-T8) ─────────────────────────────────────────────────────
// [목적] ragCandidateMetaSchema 는 tourapi.ts 단일 정의 — index.ts 의 export * 로 노출
// [이유] 버그 수정(2026-09-17): 여기서 재-export 하면 index.ts 에서 동일 이름이 두 경로로 export 되어
//         eslint import/export 에러로 workspace lint 가 실패함. 패키지 exports 는 "." 하나뿐이라 경로 import 불가.
// [사이드 이팩트] 없음 — 소비처는 모두 '@gangwoncanvas/types' 루트 import
// [수정 시 고려사항] 서브패스 exports 를 추가하게 되면 plan-stream 진입점 재-export 필요 여부 재검토

// ── SSE event 스키마 6종 (heartbeat 는 empty data — zod 정의 불필요) ──────────

// [목적] SSE `event: phase` 페이로드 zod 스키마 — 파이프라인 단계 전환 이벤트
// [이유] FE EventSource 핸들러가 phase 값으로 UI 진행 표시(로딩 라벨)를 갱신.
//         zod 파싱으로 알 수 없는 phase 값 조기 차단.
//         sse-live-typing Wave 1 (ADR-LT6, Q-3): 기존 5종 → 9종으로 확장.
//           신규 추가 — 'narration'(Q-5 cover 직후 산문 stream 진입 알림),
//                       'extracting'·'evaluating'·'generating-questions'(슬롯 SSE 턴 단계 가시화).
//         후방 호환: 구버전 FE 는 알 수 없는 phase 값을 무시하므로 배포 순서 무관.
// [사이드 이팩트] FE 진행 UI 라벨 문자열은 message 필드 또는 phase → i18n 키 매핑으로 처리.
//                 FE usePlanGenerateStream.ts 의 StreamPhase 타입은 별도 로컬 유니온 —
//                 해당 파일도 phase 유니온 확장 대상 (W7 카드 책임).
// [수정 시 고려사항] phase 유니온 추가 시 FE 진행 라벨 매핑 테이블도 동기 갱신.
//                   'slot'·'tour'·'rag'·'llm'·'persist' 5종은 생성 파이프라인(/jobs/:id/stream) 전용.
//                   'extracting'·'evaluating'·'generating-questions' 3종은 슬롯 SSE(/messages/stream) 전용.
//                   'narration' 은 생성 파이프라인 cover 직후 내레이션 진입 신호.
export const phaseEventSchema = z.object({
  type: z.literal('phase'),
  phase: z.enum([
    // 생성 파이프라인 기존 5종 (/jobs/:id/stream) — 변경 금지
    'slot',
    'tour',
    'rag',
    'llm',
    'persist',
    // sse-live-typing Wave 1 신규 추가
    'narration', // cover 직후 산문 내레이션 LLM 패스 진입 (Q-5 결정)
    'extracting', // 슬롯 SSE: 사용자 답변 슬롯 추출 단계 (Q-3 결정)
    'evaluating', // 슬롯 SSE: 부족 슬롯 평가 단계 (Q-3 결정)
    'generating-questions', // 슬롯 SSE: 후속 질문 생성 단계 (Q-3 결정)
  ]),
  message: z.string().max(200).optional(),
});

// [목적] SSE `event: text` 페이로드 zod 스키마 — LLM 토큰 단위 스트리밍 청크
// [이유] FE 가 tokenChunk 를 누적 concat 하여 채팅 버블 내 실시간 타이핑 효과 구현.
//         sectionId 로 어느 섹션이 스트리밍 중인지 식별.
// [사이드 이팩트] FE 섹션별 누적 버퍼를 Map<sectionId, string> 으로 관리 필요.
// [수정 시 고려사항] tokenChunk 는 빈 문자열 가능(LLM SDK 의 flush 이벤트).
//                   sectionId 형식은 'cover' | 'day-1' | 'day-2' | ... | 'mood' | 'budget' | 'map'.
export const textEventSchema = z.object({
  type: z.literal('text'),
  sectionId: z.string().min(1).max(40),
  tokenChunk: z.string(),
});

// [목적] SSE `event: section` 페이로드 zod 스키마 — 섹션 완료 시 구조화 데이터 전송
// [이유] 섹션별 LLM 스트리밍 완료 후 zod 파싱된 구조화 데이터를 FE 에 전달.
//         FE 는 sectionId 분기로 coverSchema/dayPlanSchema 등 적절한 스키마로 재파싱 후 렌더링.
// [사이드 이팩트] FE SectionAssembler 클라이언트 사이드 구현 필요 — payload 를 unknown 으로 수신 후 sectionId 분기.
// [수정 시 고려사항] payload 는 unknown — 실제 검증은 sectionId 분기로 SectionAssembler(FE)에서 수행.
//                   BE SectionAssemblerService 가 이미 zod 파싱을 완료한 결과를 전달하므로 FE 2차 파싱은 방어적 검증.
export const sectionEventSchema = z.object({
  type: z.literal('section'),
  sectionId: z.string().min(1).max(40),
  payload: z.unknown(), // 실제 검증은 sectionId 분기로 SectionAssembler 에서
});

// [목적] SSE `event: progress` 페이로드 zod 스키마 — 전체 파이프라인 진행률(0-100)
// [이유] FE 진행 바(ProgressBar) 컴포넌트에 직접 바인딩. 10% 단위 발생.
// [사이드 이팩트] FE 진행 바 상태는 percent 값으로만 갱신 — phase 이벤트와 독립.
// [수정 시 고려사항] percent 는 단조 증가 보장 필요 — PlanGenerateWorker 에서 이전값보다 낮은 값 publish 금지.
export const progressEventSchema = z.object({
  type: z.literal('progress'),
  percent: z.number().int().min(0).max(100),
});

// [목적] SSE `event: complete` 페이로드 zod 스키마 — 파이프라인·턴 종료 신호
// [이유] sse-live-typing Wave 1 (ADR-LT6, R-18):
//         /jobs/:id/stream complete 는 portfolioId=uuid (폴리오 생성 완료 → 라우팅).
//         /messages/stream complete 는 portfolioId=null (슬롯 SSE 턴 종료 — 폴리오 없음).
//         두 SSE 종류가 동일 complete 이벤트 타입을 공유하므로 nullable 화.
// [사이드 이팩트] FE usePlanGenerateStream.ts 의 STREAM_COMPLETE 액션 및 ChatPage.tsx 라우팅 분기에 영향.
//                 portfolioId null 수신 시 라우팅 미실행 — 소비자 null 분기 필수 (R-18).
//                 FE 소비자 회귀 차단: usePlanGenerateStream.ts L329 에서 null 분기 추가 완료 (Wave 1 동반).
// [수정 시 고려사항] portfolioId uuid = /jobs/:id/stream complete, null = /messages/stream complete.
//                   FE 가 portfolioId null 인 complete 를 라우팅 트리거로 오용하지 않도록
//                   if (portfolioId) 가드 유지 필수 (ChatPage.tsx L240 · ChatMobile.tsx L260 현재 구현 충족).
export const completeEventSchema = z.object({
  type: z.literal('complete'),
  // sse-live-typing Wave 1 (R-18): nullable 화 — /messages/stream complete 는 null 발행
  portfolioId: z.string().uuid().nullable(),
});

// [목적] SSE `event: error` 페이로드 zod 스키마 — 파이프라인 오류 (연결 종료)
// [이유] FE 가 code 로 오류 유형을 식별하여 적절한 에러 UI(재시도/취소/안내)를 표시.
//         code 유니온으로 LlmStreamService fallback 분기 식별에도 활용.
// [사이드 이팩트] FE EventSource 는 error 이벤트 수신 후 .close() + 에러 토스트 표시.
// [수정 시 고려사항] code 추가 시 LlmStreamService fallback 분기 + FE 에러 메시지 i18n 키 동기 갱신.
export const errorEventSchema = z.object({
  type: z.literal('error'),
  code: z.enum([
    'LLM_TIMEOUT',
    'PROVIDER_ERROR',
    'RATE_LIMIT',
    'SCHEMA_VIOLATION',
    'RAG_EMPTY',
    'USER_CANCELLED',
  ]),
  message: z.string().max(500),
});

// ── addons 스키마 (CPB Wave 2 — conversational-polio-builder §3 자유 요청) ─────

// [목적] 사용자 자유 요청(슬롯 외 추가 의도)을 type-discriminated 구조로 분류하는 zod 스키마
// [이유] CPB Spec §1-1-(3) Q-4 확정: 8슬롯 외 비구조화 요청을 'addons' 컬렉션으로 별도 수용.
//         LLM extract_slots callTool 이 슬롯 추출과 동일 호출 내에서 addons 필드를 함께 출력하며,
//         AiSession.slots.__addons JSONB 메타 키로 영속화 → 폴리오 생성 시 RAG 가중치·LLM 프롬프트에 주입.
// [사이드 이팩트] SlotExtractorService.mergeExtractSlots 가 이 스키마로 LLM addons 응답을 파싱.
//                 PlanGenerateService.generate() 가 session.slots.__addons 를 읽어
//                   (a) RagCandidateService.getCandidates(slots, addons) 가중치 입력,
//                   (b) buildLlmContext(slots, candidates, addons) LLM 시스템 프롬프트 주입,
//                   (c) buildDayUserPrompt(..., addons) day-N day_pin 주입에 사용.
//                 FE GaugeProgressPanel 의 addons 카드 리스트 렌더링 데이터 소스.
// [수정 시 고려사항] type 유니온 추가 시 evaluateGaps/RAG 가중치 로직 동기 갱신 필요.
//                   text min(1).max(200) — LLM 프롬프트 토큰 예산과 연동.
//                   dayIndex min(0).max(14) 은 slotsSchema.durationDays 상한(14)과 정합.
//                   'day_pin' 은 dayIndex 필수 — 해당 날짜 일정에 직접 주입되므로 누락 불허.
//                   'preference' 는 전체 여행에 적용되므로 dayIndex 없음.
export const addonSchema = z.discriminatedUnion('type', [
  // poi_request: 특정 날짜 또는 전체 일정에서 선호 POI 유형/장소 요청 (예: "1일차에 맛집")
  z.object({
    type: z.literal('poi_request'),
    text: z.string().min(1).max(200),
    dayIndex: z.number().int().min(0).max(14).optional(),
    category: z.string().optional(),
  }),
  // avoid: 특정 날짜 또는 전체 일정에서 회피할 POI 유형/장소 (예: "번잡한 관광지 빼줘")
  z.object({
    type: z.literal('avoid'),
    text: z.string().min(1).max(200),
    dayIndex: z.number().int().min(0).max(14).optional(),
    category: z.string().optional(),
  }),
  // preference: 전체 여행 선호 사항 — 특정 날짜 지정 없이 전반적 조건 (예: "조용한 분위기 선호")
  z.object({
    type: z.literal('preference'),
    text: z.string().min(1).max(200),
    category: z.string().optional(),
    // dayIndex 없음 — 전체 여행에 적용
  }),
  // day_pin: 특정 날짜에 고정 요청 — dayIndex 필수 (예: "2일차에 속초 해수욕장 꼭 포함")
  z.object({
    type: z.literal('day_pin'),
    text: z.string().min(1).max(200),
    dayIndex: z.number().int().min(0).max(14),
    // category 없음 — POI 특정보다 날짜 고정이 핵심
  }),
]);

// [목적] addons 배열 스키마 — 한 세션당 최대 10건 제한
// [이유] WC-AVCF-01: 상한을 max(5) → max(10) 으로 갱신.
//         이유: __addons 영속 상한을 10으로 확장했는데(avoid 수집 R-4 번복 결정, 상한 10·중복제거·FIFO 동반 복원)
//         addonsSchema.max(5) 유지 시 6번째 avoid 항목부터 safeParse 실패 → [] 폴백 → 수집 손실 발생.
//         영속(SlotExtractorService mergeAddons) · 검증(addonsSchema.safeParse) · 응답(slotFillingStateSchema)
//         이 모두 동일 스키마를 참조하므로 이 한 곳만 변경하면 전 경로 정합.
//         slotFillingStateSchema.addons 는 이 스키마를 직접 참조 → 자동 정합.
// [사이드 이팩트] slotFillingStateSchema.addons 필드의 스키마 소스 (자동 반영).
//                 plan-generate.service.ts 의 addonsSchema.safeParse 경로도 10건 상한으로 자동 적용.
// [수정 시 고려사항] max(10) 변경 시 SlotExtractorService FIFO 상수(mergeAddons 상한)도 동기 갱신 필요(WC-AVCF-03).
//                   max 축소는 영속된 __addons 가 해당 수를 초과하면 응답 파싱 실패 → 반드시 DB 확인 후 변경.
export const addonsSchema = z.array(addonSchema).max(10);

// ── 파생 타입 (addonSchema / addonsSchema) ────────────────────────────────────
export type Addon = z.infer<typeof addonSchema>;
export type Addons = z.infer<typeof addonsSchema>;

// ── 슬롯 대화 스키마 (ADR-CSF7 — conversational-slot-filling Wave 1) ───────────

// [목적] 대화형 슬롯 채우기에서 되물을 수 있는 슬롯 키 11종 유니온 스키마
// [이유] BE SlotGapService.evaluateGaps() + generateFollowUps() 가 slotKey 로 부족 슬롯을 식별.
//         FE FollowUpQuestion.tsx 가 slotKey 로 질문 대상 슬롯을 표시.
//         ADR-CSF7 — duration 키 ↔ slotsSchema.durationDays 필드 불일치는 의도된 설계.
//         매핑(SLOT_KEY_TO_FIELD)은 W3 SlotGapService 책임이며 본 Wave 는 스키마 정의만.
//         ADR-FGQ-OQ2·AD-TR-FGQ3: 'startDate'·'timeOfDayPref' 2키 추가 — 선택 수집 슬롯(8→10키).
//           REQUIRED_SLOTS 4종(destination/duration/companions/vibe)·sufficient·TURN_LIMIT 불변.
//         WC-AVCF-02: 'rentalCost' 추가 (10→11키) — transport='rent' 시 조건부 되묻기 대상 키.
//           REQUIRED_SLOTS 에 포함하지 않음 — transport='rent' 조건부 선택 슬롯 전용.
// [사이드 이팩트] slotKeySchema 변경 시 SlotGapService 매핑 상수(SLOT_KEY_TO_FIELD·FOLLOW_UP_FALLBACK_FIXTURE) +
//                 FE slotLabels.ts 라벨 i18n 키 + TripInfoCard 표시 동기 갱신 필요(T7·T10 책임).
//                 WC-AVCF-06: slot-gap.service.ts 에 'rentalCost' 매핑 + fixture 추가 필요.
//                 WC-AVCF-09: FE slotLabels.ts SLOT_KEY_LABEL·SLOT_ORDER·isSlotFilled 에 'rentalCost' 반영 필요.
// [수정 시 고려사항] 12키 유니온 — slotsSchema 에 새 필드 추가 시 이 유니온도 함께 확장.
//                   'duration' 는 slotsSchema.durationDays 에 대응 (불일치 의도적 — ADR-CSF7).
//                   'startDate' 는 slotsSchema.startDate 에 대응 (선택 수집).
//                   'timeOfDayPref' 는 slotsSchema.timeOfDayPref 에 대응 (선택 수집).
//                   'rentalCost' 는 slotsSchema.rentalCost 에 대응 (transport='rent' 조건부 수집).
//                   'departureTime' 는 slotsSchema.departureTime 에 대응 (sufficient=true 이후 선택 조건부 수집).
//                   'rentalCost'·'startDate'·'timeOfDayPref'·'departureTime' 는 REQUIRED_SLOTS 에 포함하면 안 됨 — 조건부/선택 게이트 전용.
// [목적] 슬롯 키 유니온 스키마 — SLOT_KEY_TO_FIELD/FOLLOW_UP_FALLBACK_FIXTURE Record<SlotKey,...> 의 완전성 강제
// [이유] T1 (2026-05-31): departureTime 슬롯 신설로 11키 → 12키. Record<SlotKey, ...> 사용처에서 tsc 가 누락 키를 자동 검출.
// [사이드 이팩트] slotKeySchema 소비자(slot-gap.service/slot-extractor.service/plan-generate.controller) 전부 12키 동기 갱신 필요.
// [수정 시 고려사항] 키 추가 시 SLOT_KEY_TO_FIELD(slot-gap.service.ts)/FOLLOW_UP_FALLBACK_FIXTURE(slot-gap.service.ts)/
//                   SCALAR_FIELDS(slot-extractor.service.ts)/buildSlotsJsonSchema(slot-extractor.service.ts) 6곳 동기 갱신 필수.
export const slotKeySchema = z.enum([
  'destination',
  'headcount',
  'companions',
  'vibe',
  'budget',
  'transport',
  'origin',
  'duration',
  // ADR-FGQ-OQ2·AD-TR-FGQ3: 선택 수집 슬롯 2키 (필수 게이트 불변)
  'startDate',
  'timeOfDayPref',
  // WC-AVCF-02: transport='rent' 조건부 되묻기 슬롯 키 (REQUIRED_SLOTS 제외 — 조건부 전용)
  'rentalCost',
  // T1 (2026-05-31): 출발 시각 선택 조건부 슬롯 키 (REQUIRED_SLOTS 제외 — sufficient=true 이후 1회 질문)
  'departureTime',
]);

// [목적] 후속 질문 1건 구조 zod 스키마 — 슬롯 키 + 자연어 질문 + 보기 칩 배열
// [이유] BE SlotGapService.generateFollowUps() 가 LLM callTool 응답을 이 스키마로 강제 파싱하여
//         환각된 slotKey·비정상 구조를 조기 차단 (CLAUDE §6-5 LLM zod 강제 정책).
//         FE FollowUpQuestion.tsx 가 이 타입을 받아 질문 버블 + 칩 리스트를 렌더링.
// [사이드 이팩트] suggestedChips 배열이 FE 칩 렌더러에 직접 바인딩됨.
//                 question max(200) 은 FE 버블 UI 줄바꿈 임계값과 연동.
// [수정 시 고려사항] suggestedChips max(6) 은 FE 칩 레이아웃 상한 — 변경 시 디자인 토큰도 검토.
//                   slotKey 는 slotKeySchema 유니온만 허용 — 자유 문자열 불허.
export const followUpQuestionSchema = z.object({
  slotKey: slotKeySchema,
  question: z.string().min(1).max(200),
  suggestedChips: z.array(z.string()).max(6),
});

// [목적] 후속 질문 배열 zod 스키마 — 한 turn 당 최대 3개 제한
// [이유] ADR-CSF7·Q-2 확정 — 한 turn 에 최대 3개 질문을 묶어 제시.
//         필수 슬롯 우선으로 상위 3개만 전달하여 사용자 부담 최소화.
//         BE SlotGapService.generateFollowUps() 반환값 + POST /messages 응답 필드.
// [사이드 이팩트] FE ChatPage 가 이 배열을 순서대로 렌더링 — 빈 배열 시 질문 버블 미노출.
// [수정 시 고려사항] max(3) 변경 시 Spec Q-2 결정 갱신 + FE 레이아웃 재검토 필요.
export const followUpQuestionsSchema = z.array(followUpQuestionSchema).max(3);

// [목적] 슬롯 채우기 대화의 현재 상태 전체 zod 스키마 — POST /messages · GET /sessions/:id 공통 응답
// [이유] BE PlanGenerateController 가 /messages 처리 후 이 구조로 응답하여
//         FE 가 단일 타입으로 슬롯 상태·부족 슬롯·후속 질문·진행 턴·ready 여부를 일괄 수신.
//         ready=true 시 FE 가 즉시 /start → SSE 생성 단계로 전이 가능.
//         CPB Wave 2: addons 필드 추가 — optional().default([]) 로 기존 소비자 회귀 0 (R-18 패턴).
//         WC-CTX-03: confirmDecision 필드 추가 — confirm 컨텍스트 분기 결과 전달.
//           optional 로 추가(후방호환) — confirmContext 없는 기존 응답 소비자 영향 0.
//         WA7 (2026-06-01 chat-slot-avoid-enhancement): originGuidance/departureTimeGuidance 안내 필드 추가.
//           origin 기본값('서울') 적용 시 originGuidance, departureTime 기본값('09:00') 적용 시 departureTimeGuidance.
//           optional(후방호환) — 기존 소비자는 undefined 로 수신(무시).
//           FE 가 이 필드 존재 시 안내 말풍선("서울 출발 기준" / "09:00 기본값") 을 대화에 렌더.
// [사이드 이팩트] FE usePlanSlotFilling hook 이 이 타입을 상태로 관리.
//                 ready=true 전이 시 FE ChatPage 생성 단계 진입 트리거.
//                 addons 필드 추가로 slotFillingStateEventSchema 의 state 가 addons 포함 — FE 소비자 자동 반영.
//                 GaugeProgressPanel 의 addons 카드 리스트가 이 필드를 구독.
//                 confirmDecision='generate' 수신 시 FE 가 /start 를 호출하는 진입점이 됨.
//                 WA7: originGuidance/departureTimeGuidance 수신 시 ChatPage/ChatMobile 이 안내 말풍선 렌더.
//                 WC3: avoidEchoMessage 수신 시 ChatPage/ChatMobile 이 avoid echo 말풍선 렌더.
// [수정 시 고려사항] turnCount 는 slots.__turnCount JSONB 메타 키로 영속화 (ADR-CSF5).
//                   slotsSchema 변경 시 이 스키마의 slots 필드도 자동 반영 (z.infer 파생).
//                   missingSlots 는 slotKeySchema 배열 — 필수 3종(destination·duration·companions) 포함 여부로 ready 판단.
//                   addons: optional().default([]) — Wave 2 이전 응답(addons 없음)도 [] 로 파싱 보장 (R-18 패턴).
//                   addons 상한(5) 은 addonsSchema.max(5) 에서 관리 — 여기서 별도 상한 불필요.
//                   confirmDecision: 'generate'|'refine'|'reask' — confirmContext=true 일 때만 설정됨.
//                     generate: affirm + 슬롯 충분(evaluateGaps sufficient) → FE /start 호출 신호.
//                     refine:   deny → 보강 흐름(선택 슬롯 되묻기·자유 입력 유도).
//                     reask:    other 또는 affirm+슬롯 불충분 → confirm 재노출 안내.
//                   originGuidance/departureTimeGuidance: TURN_LIMIT 도달 기본값 채움 경로에서만 설정됨.
//                     FE 는 undefined 이면 안내 미표시, string 이면 봇 안내 말풍선으로 렌더.
//                   avoidEchoMessage: avoid 명확(clear) 밴드 시 "○○ 빼드릴게요 👍" 류 에코 문구 — optional(후방호환).
//                     FE 는 undefined 이면 미표시, string 이면 role:'bot' 말풍선으로 렌더(검색·출처 없는 안내 톤).
//                     LLM 자유생성 아닌 정적/구조화 템플릿 문구(BE WC2 — 환각 0, XSS 텍스트 노드 바인딩으로 방어).
export const slotFillingStateSchema = z.object({
  slots: slotsSchema,
  missingSlots: z.array(slotKeySchema),
  followUpQuestions: followUpQuestionsSchema,
  turnCount: z.number().int().min(0),
  ready: z.boolean(),
  // CPB Wave 2 (ADR-CPB6): 자유 요청 addons 누적 — optional default [] 로 기존 소비자 회귀 0
  addons: addonsSchema.optional().default([]),
  // WC-CTX-03: confirm 컨텍스트 분기 결과 — optional(후방호환). confirmContext=true 턴에만 설정.
  confirmDecision: z.enum(['generate', 'refine', 'reask']).optional(),
  // WA7 (2026-06-01): origin 기본값('서울') 적용 시 안내 문구 — optional(후방호환).
  // FE 는 이 필드 존재 시 "서울 출발 기준으로 잡았어요" 류 봇 안내 말풍선을 대화에 렌더.
  originGuidance: z.string().optional(),
  // WA7 (2026-06-01): departureTime 기본값('09:00') 적용 시 안내 문구 — optional(후방호환).
  // FE 는 이 필드 존재 시 "출발 시각을 09:00 기본값으로 잡아드렸어요" 류 봇 안내 말풍선 렌더.
  departureTimeGuidance: z.string().optional(),
  // WC3 (2026-06-01): avoid 명확(clear) 밴드 에코 문구 — optional(후방호환).
  // BE WC2: 매칭 band='clear' 시 "○○ 빼드릴게요 👍" 류 정적 템플릿 문구 반환.
  // FE 는 이 필드 존재 시 role:'bot' 말풍선으로 렌더. LLM 환각 없음 — 텍스트 노드 바인딩으로 XSS 방어.
  // avoidEchoMessage 존재 여부와 무관하게 기존 confirmDecision/followUpQuestions 흐름 불변(union 불변).
  avoidEchoMessage: z.string().optional(),
});

// [목적] POST /plan/sessions/:id/messages 요청 바디 zod 스키마 — 후속 답변 1건
// [이유] 사용자 답변이 자유 텍스트(answerText) 또는 칩 선택(selectedChips) 두 경로로 수신됨.
//         superRefine 으로 둘 중 최소 하나가 반드시 존재하도록 강제하여 빈 요청 차단.
//         BE ZodValidationPipe 가 입력 검증에 사용. FE postPlanMessage() 클라이언트도 동일 타입 사용.
//         WC-CTX-03: confirmContext 필드 추가 — FE 가 confirm 버블 직후 입력일 때 true 로 설정.
//           optional(기본 false) 로 후방호환 — 기존 FE 요청(confirmContext 없음)도 파싱 성공.
// [사이드 이팩트] PlanGenerateController.postMessage() @Body() DTO 소스가 됨.
//                 텍스트·칩 병존 가능 — SlotExtractorService 가 둘을 합산하여 LLM 프롬프트 구성.
//                 confirmContext=true 수신 시 컨트롤러가 classifyConfirmIntent 분기로 진입.
// [수정 시 고려사항] answerText max(1000) 은 CLAUDE §createSessionInputSchema 의 inputText max 와 통일.
//                   selectedChips max(8) 은 FE 칩 렌더러 상한보다 넉넉하게 설정 (방어적 상한).
//                   superRefine 조건 변경 시 FE 폼 유효성 검사 로직도 함께 갱신 필요.
//                   confirmContext 는 FE 가 직전 메시지 role='confirm' 여부를 기준으로 설정 — BE 는 서버 검증(Low-2)으로 2차 방어.
export const postMessageInputSchema = z
  .object({
    answerText: z.string().max(1000).optional(),
    selectedChips: z.array(z.string()).max(8).optional(),
    // WC-CTX-03: confirm 버블 직후 입력 플래그 — optional(undefined == false). 후방호환(기존 FE 요청 영향 0).
    // z.boolean().optional() 로 선언: TypeScript infer 결과 boolean|undefined — 기존 테스트 픽스처({ answerText }) 파싱 정상.
    // z.default() 사용 시 infer 결과가 boolean(required) 이 되어 기존 코드 타입 오류 발생 → optional() 단독 사용.
    confirmContext: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    const hasText = val.answerText !== undefined && val.answerText.length > 0;
    const hasChips = val.selectedChips !== undefined && val.selectedChips.length > 0;
    if (!hasText && !hasChips) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'answerText 또는 selectedChips 중 최소 하나는 필수입니다.',
      });
    }
  });

// ── SSE event union (슬롯 대화 스키마 이후 배치 — slotFillingStateSchema 의존) ──

// [목적] SSE `event: slotFillingState` 페이로드 zod 스키마 — 슬롯 SSE 턴 최종 상태 이벤트 (신규, ADR-LT6)
// [이유] sse-live-typing Spec §2-1(A): /messages/stream 의 슬롯 SSE 턴 마지막에
//         SlotFillingState 전체를 1회 발행하여 FE 가 슬롯 상태를 일괄 갱신.
//         text 이벤트(토큰 단위 질문 텍스트) + 최종 slotFillingState 이벤트(구조화 최종 상태)
//         의 2-페이즈 패턴으로 LLM 환각은 final 이벤트 zod 강제로만 차단 (ADR-CSF4 정책 유지).
//         slotFillingStateSchema 의존으로 반드시 해당 스키마 선언 이후에 배치.
// [사이드 이팩트] FE usePlanSlotFilling 훅이 이 이벤트를 react-query 캐시 setQueryData 트리거로 사용.
//                 slotFillingStateSchema 는 동일 파일 내 재사용 — import 0건.
// [수정 시 고려사항] state 필드는 slotFillingStateSchema 전체 — 소비자가 partial 업데이트 아닌 전체 교체 기준으로 처리.
//                   이 이벤트는 /messages/stream 전용 — /jobs/:id/stream 에서는 발행 금지.
export const slotFillingStateEventSchema = z.object({
  type: z.literal('slotFillingState'),
  state: slotFillingStateSchema,
});

// [목적] 모든 SSE 이벤트의 discriminated union — FE EventSource 핸들러 타입 가드 소스
// [이유] `type` 필드로 discriminate 하여 FE switch/if 분기를 exhaustive check 가능하게 함.
//         zod.discriminatedUnion 은 런타임 파싱 + 타입 narrowing 을 동시에 제공.
//         sse-live-typing Wave 1 (ADR-LT6): slotFillingStateEventSchema 를 7번째로 추가.
//         backward-compatible — 구버전 FE 는 알 수 없는 type 을 safeParse 실패로 무시.
// [사이드 이팩트] 새 이벤트 타입 추가 시 본 union + FE switch 분기 모두 갱신 필요.
//                 planStreamEventSchema 파생 타입 PlanStreamEvent 에 SlotFillingStateEvent 가 자동 포함됨.
// [수정 시 고려사항] heartbeat 는 empty data 이므로 union 에서 제외 — FE 는 event: heartbeat 를 무시 처리.
//                   discriminatedUnion 의 discriminant 키는 'type' 고정 — 모든 멤버 스키마에 z.literal('type값') 필수.
//                   slotFillingStateEventSchema 는 slotFillingStateSchema 를 의존하므로
//                   이 블록은 반드시 슬롯 대화 스키마 섹션 이후에 위치해야 함.
export const planStreamEventSchema = z.discriminatedUnion('type', [
  phaseEventSchema,
  textEventSchema,
  sectionEventSchema,
  progressEventSchema,
  completeEventSchema,
  errorEventSchema,
  slotFillingStateEventSchema, // sse-live-typing Wave 1 신규 추가 (ADR-LT6)
]);

// ── 챗봇 메시지 응답 discriminated union (chat-web-search-fallback T5) ───────────

// [목적] POST /sessions/:id/messages 응답 3-way discriminated union — slot | knowledge | refusal
// [이유] chat-web-search-fallback Spec T5 AD-T4: kind discriminant 으로 세 응답 타입 명확 분기.
//         (1) slot: 기존 SlotFillingState 그대로(kind 생략 또는 optional "slot") — slot 경로 바이트 동일 후방호환 필수(R5).
//         (2) knowledge: 강원 지식 Q&A 응답 — answer 본문 + citations[] 출처(url_citation 파싱 결과).
//         (3) refusal: out_of_scope 가드레일 거절 응답 — 정적 본문 + citations 없음.
//         kind 필드를 optional("slot") 으로 두어 기존 SlotFillingState 소비자(usePlanSlotFilling 등) 회귀 0.
// [사이드 이팩트] FE usePlanSlotFilling hook 이 kind 필드 분기로 3-way 응답 처리.
//                 kind 없거나 "slot" 이면 기존 SlotFillingState 흐름 그대로 유지(ready=true → /start 전이 불변).
//                 KnowledgeAnswerResponse / ScopeRefusalResponse 는 ready/slots/followUpQuestions 없음.
// [수정 시 고려사항] SlotFillingState 필드(slots/missingSlots/followUpQuestions/turnCount/ready/addons) 불변 — 변경 절대 금지(R5).
//                   UrlCitation 은 openrouter.client.ts 의 web-search.schema.ts UrlCitation 과 형태 일치 유지.
//                   MessageResponse union 확장 시 FE switch/if 분기 동기 갱신 필요.

/**
 * [목적] url_citation 출처 타입 — web-search.schema.ts 의 urlCitationSchema 와 형태 일치
 * [이유] BE/FE 공유 — @gangwoncanvas/types 에서 단일 정의하여 두 패키지의 UrlCitation 동기.
 * [사이드 이팩트] openrouter.client.ts 의 UrlCitation(types.ts) 과 구조 동일 유지 필수.
 * [수정 시 고려사항] OpenRouter url_citation 구조 변경 시 이 타입과 web-search.schema.ts 동기 갱신 필요.
 *                   url 은 http(s) 스킴만 허용(Low-1, security-scan W1) — z.string().url() 단독은
 *                   javascript:/data: 등을 통과시켜 FE 링크 렌더 시 DOM XSS 표면. refine 으로 사전 차단.
 *                   web-search.schema.ts(BE) urlCitationSchema 와 동일 정책 유지.
 */
export const urlCitationSchema = z.object({
  url: z
    .string()
    .url()
    .refine((u) => /^https?:\/\//i.test(u), {
      message: 'url must use http(s) scheme',
    }),
  title: z.string().optional(),
  content: z.string().optional(),
  start_index: z.number().int().optional(),
  end_index: z.number().int().optional(),
});
export type UrlCitation = z.infer<typeof urlCitationSchema>;

/**
 * [목적] 강원 지식 Q&A 응답 스키마 — knowledge_in_domain 분기 응답 타입
 * [이유] intent=knowledge_in_domain → KNN 조회 → (miss 시) 웹서치 → 출처와 함께 답변 반환.
 *         citations 는 url_citation annotations 파싱 결과 — 웹서치 미발동 시 빈 배열 가능.
 * [사이드 이팩트] FE 가 kind:"knowledge" 로 분기하여 answer 본문 + citations 링크 목록 렌더.
 * [수정 시 고려사항] answer 는 LLM 생성 텍스트 — 사실 필드(factInfo) 로 머지 금지(INV-2).
 */
export const knowledgeAnswerResponseSchema = z.object({
  kind: z.literal('knowledge'),
  answer: z.string(),
  citations: z.array(urlCitationSchema),
});
export type KnowledgeAnswerResponse = z.infer<typeof knowledgeAnswerResponseSchema>;

/**
 * [목적] out_of_scope 가드레일 거절 응답 스키마 — 정적 거절 템플릿 + 여행 기획 재유도
 * [이유] intent=out_of_scope → 웹서치/LLM 자유생성 없이 정적 거절 템플릿으로만 응답(INV-3).
 *         citations 없음 — 검색 미발동. message 는 scope-guard.refusal.ts 상수.
 * [사이드 이팩트] FE 가 kind:"refusal" 로 분기하여 검색·출처 없는 정적 안내 말풍선 표시.
 * [수정 시 고려사항] message 는 LLM 환각 방지를 위해 반드시 정적/반결정적 템플릿 문구여야 함.
 *                   citations 필드 추가 금지 — 거절 응답에 외부 링크 포함 불가(가드레일 원칙).
 */
export const scopeRefusalResponseSchema = z.object({
  kind: z.literal('refusal'),
  message: z.string(),
});
export type ScopeRefusalResponse = z.infer<typeof scopeRefusalResponseSchema>;

/**
 * [목적] intent enum 3분류 스키마 — slotsSchema 에 optional 로 선반영(W2 에서 LLM 추출)
 * [이유] chat-web-search-fallback T5 AD-T2: buildSlotsJsonSchema() 에 intent 필드 동시 추출(W2 T6).
 *         W1 에서 타입 선반영(optional 후방호환) → W2 에서 실제 LLM 추출 구현.
 *         optional 로 선언: 기존 slotsSchema 소비자(slot-extractor, plan-generate 등) 회귀 0.
 * [사이드 이팩트] slotsSchema 에 intent 필드 추가 — optional 이므로 기존 AiSession.slots JSONB 파싱 무실패(후방호환).
 *                 W2 T6 구현 후 SlotExtractorService 가 이 enum 으로 LLM 응답 intent 파싱.
 * [수정 시 고려사항] out_of_scope 추가(기존 2값 → 3값) — 기존 테스트 픽스처에 intent 없어도 optional 이므로 파싱 성공.
 *                   불명확 시 안전 기본값 "slot" 폴백은 SlotExtractorService 에서 처리(W2 T7).
 */
export const intentSchema = z.enum(['slot', 'knowledge_in_domain', 'out_of_scope']);
export type Intent = z.infer<typeof intentSchema>;

// ── 타입 export ───────────────────────────────────────────────────────────────

// [목적] plan-stream.ts 스키마에서 파생된 TypeScript 타입 일괄 export
// [이유] BE/FE 모두 zod 스키마 대신 타입만 필요한 곳(함수 시그니처, 인터페이스 등)에서 사용.
//         sse-live-typing Wave 1 (ADR-LT6): SlotFillingStateEvent 타입 신규 추가.
//         Phase 타입도 추가하여 FE/BE 가 phase 값 유니온을 공유 타입으로 참조 가능.
// [사이드 이팩트] 스키마명 변경 시 타입명도 함께 갱신 필요.
//                 PlanStreamEvent 유니온에 SlotFillingStateEvent 가 포함되므로
//                 기존 소비자의 switch exhaustive check 에 새 케이스 추가 필요 (W7 카드 책임).
// [수정 시 고려사항] StreamDayPlanItem / StreamDayPlan 은 folio.ts 의 PlanItem / DayPlan 과 별개임을 주석·문서로 명시.
//                   SlotKey · FollowUpQuestion · SlotFillingState · PostMessageInput 은 ADR-CSF7 슬롯 대화 타입.
//                   SlotFillingStateEvent 는 /messages/stream 전용 — /jobs/:id/stream 소비자는 이 타입 무시.
export type CreateSessionInput = z.infer<typeof createSessionInputSchema>;
export type Slots = z.infer<typeof slotsSchema>;
export type Cover = z.infer<typeof coverSchema>;
export type StreamDayPlanItem = z.infer<typeof streamDayPlanItemSchema>;
export type StreamDayPlan = z.infer<typeof streamDayPlanSchema>;
export type FolioMap = z.infer<typeof mapSchema>;
export type Mood = z.infer<typeof moodSchema>;
// WC-CTX-08: transitSummarySchema 파생 타입 — estimateTransitCost 반환과 1:1 대응
export type TransitSummary = z.infer<typeof transitSummarySchema>;
export type Budget = z.infer<typeof budgetSchema>;
export type PlanStreamEvent = z.infer<typeof planStreamEventSchema>;
// sse-live-typing Wave 1 신규 파생 타입 (ADR-LT6)
export type Phase = z.infer<typeof phaseEventSchema>['phase'];
export type SlotFillingStateEvent = z.infer<typeof slotFillingStateEventSchema>;
// ADR-CSF7 슬롯 대화 파생 타입
export type SlotKey = z.infer<typeof slotKeySchema>;
export type FollowUpQuestion = z.infer<typeof followUpQuestionSchema>;
export type FollowUpQuestions = z.infer<typeof followUpQuestionsSchema>;
export type SlotFillingState = z.infer<typeof slotFillingStateSchema>;
export type PostMessageInput = z.infer<typeof postMessageInputSchema>;
// chat-web-search-fallback T5 신규 타입 (2026-06-01)
// UrlCitation / KnowledgeAnswerResponse / ScopeRefusalResponse / Intent 은 위 스키마 선언부에서 이미 export 됨

/**
 * [목적] POST /sessions/:id/messages 응답 3-way discriminated union 스키마
 * [이유] chat-web-search-fallback T14: kind discriminant 으로 세 응답 타입 명확 분기.
 *         (1) slot: 기존 SlotFillingState 그대로(kind 없음 → slot 경로 후방호환 100%).
 *         (2) knowledge: 지식 Q&A 응답 — answer + citations[].
 *         (3) refusal: out_of_scope 거절 — message 정적 본문.
 *         SlotFillingState 는 kind 필드가 없거나 'slot' 인 경우이므로 z.union 으로 처리.
 * [사이드 이팩트] FE usePlanSlotFilling 의 sendMessage 반환 타입이 이 union 으로 변경됨.
 * [수정 시 고려사항] slot 경로는 반드시 후방호환 — SlotFillingState 스키마 필드 변경 절대 금지(R5).
 *                   knowledge/refusal 경로는 ready/slots 필드 없음 — hook 에서 슬롯 상태 갱신 금지.
 */
export const messageResponseSchema = z.union([
  // slot 경로: 기존 SlotFillingState (kind 없거나 "slot" — 후방호환)
  slotFillingStateSchema,
  // knowledge 경로: 지식 Q&A 응답
  knowledgeAnswerResponseSchema,
  // refusal 경로: 가드레일 거절
  scopeRefusalResponseSchema,
]);

/**
 * MessageResponse = /sessions/:id/messages 응답 discriminated union
 * kind 없음 | 'slot'  → SlotFillingState (기존 흐름 후방호환)
 * kind:'knowledge'    → KnowledgeAnswerResponse
 * kind:'refusal'      → ScopeRefusalResponse
 */
export type MessageResponse = z.infer<typeof messageResponseSchema>;
